import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Resource {
  id: string
  name: string
  type: string
  provider: string
  tier: string
  region: string
  monthly_cost: number
  usage_percentage: number
  allocated: Record<string, number>
  used: Record<string, number>
  status: string
  optimization_potential: number
}

interface Recommendation {
  id: string
  resource_id: string
  type: string
  title: string
  description: string
  potential_savings: number
  effort: string
  risk: string
  status: string
}

export default function ResourceAnalyticsPage() {
  const [resources, setResources] = useState<Resource[]>([])
  const [costBreakdown, setCostBreakdown] = useState<any[]>([])
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [realtimeUsage, setRealtimeUsage] = useState<any>(null)

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchRealtime, 5000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    const [resRes, costRes, recRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/resource-analytics/resources'),
      fetch(API_BASE + '/resource-analytics/cost-breakdown'),
      fetch(API_BASE + '/resource-analytics/recommendations'),
      fetch(API_BASE + '/resource-analytics/statistics')
    ])
    if (resRes.ok) setResources((await resRes.json()).resources || [])
    if (costRes.ok) setCostBreakdown((await costRes.json()).breakdown || [])
    if (recRes.ok) setRecommendations((await recRes.json()).recommendations || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    fetchRealtime()
  }

  const fetchRealtime = async () => {
    const res = await fetch(API_BASE + '/resource-analytics/realtime')
    if (res.ok) setRealtimeUsage(await res.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800'
      case 'underutilized': return 'bg-yellow-100 text-yellow-800'
      case 'overloaded': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Resource Usage Analytics</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Resources</div>
              <div className="text-2xl font-bold">{statistics.total_resources}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Monthly Cost</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.monthly_cost)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Potential Savings</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.potential_savings)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Utilization</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.avg_utilization}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Underutilized</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.underutilized_resources}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">vs Budget</div>
              <div className="text-2xl font-bold">{statistics.cost_vs_budget}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Resources</h2></div>
            <div className="divide-y">
              {resources.map(res => (
                <div key={res.id} className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-semibold">{res.name}</div>
                      <div className="text-sm text-gray-500">{res.provider} - {res.tier}</div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(res.status)}`}>{res.status}</span>
                      <div className="text-lg font-semibold mt-1">{formatCurrency(res.monthly_cost)}/mo</div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Utilization</span>
                      <span>{res.usage_percentage}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${res.usage_percentage > 80 ? 'bg-red-500' : res.usage_percentage < 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${res.usage_percentage}%` }}></div>
                    </div>
                  </div>
                  {res.optimization_potential > 0 && (
                    <div className="text-sm text-green-600">Optimization potential: {res.optimization_potential}%</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Cost Breakdown</h3>
              <div className="space-y-3">
                {costBreakdown.map((cat, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{cat.category}</span>
                      <span className="font-semibold">{formatCurrency(cat.amount)}</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 rounded-full" style={{ width: `${cat.percentage}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {realtimeUsage && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Live Usage</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded text-center">
                    <div className="text-xl font-bold">{realtimeUsage.compute?.cpu_percentage}%</div>
                    <div className="text-xs text-gray-500">CPU</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded text-center">
                    <div className="text-xl font-bold">{realtimeUsage.compute?.memory_percentage}%</div>
                    <div className="text-xs text-gray-500">Memory</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded text-center">
                    <div className="text-xl font-bold">{realtimeUsage.database?.connections}</div>
                    <div className="text-xs text-gray-500">DB Connections</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded text-center">
                    <div className="text-xl font-bold">{realtimeUsage.cache?.hit_rate}%</div>
                    <div className="text-xs text-gray-500">Cache Hit</div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
              <div className="space-y-3">
                {recommendations.filter(r => r.status === 'pending').slice(0, 4).map(rec => (
                  <div key={rec.id} className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-sm">{rec.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{rec.description}</div>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-green-600">Save {formatCurrency(rec.potential_savings)}</span>
                      <span className="text-gray-500 capitalize">{rec.effort} effort</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
